import React, { useCallback, useEffect, useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Logger from "../../../services/Logger";
import ConfirmDialog from "../../components/ConfirmDialog";
import AdminSlidingService from "../AdminSlidingService";
import AdminStoryService from "../AdminStoryService";
import { getStoryFreeCheckboxState } from "../adminSlidingUi";
import SlidingPlaySummaryRow from "../play/SlidingPlaySummaryRow";
import StoryImageZoomDialog from "../StoryImageZoomDialog";
import AdminSlidingPackService from "./AdminSlidingPackService";
import { bandLabel, slidingPackBands } from "./slidingPackBands";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const PACK_TABS = ["fill", "check", "stats"];

const IMAGE_STATE_OPTIONS = [
    { value: "", label: "All" },
    { value: "REVIEWED", label: "Reviewed" },
    { value: "PUBLISHED", label: "Published" },
];

const IMAGE_SORT_OPTIONS = [
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
];

const IMAGE_SORT_VALUES = new Set(IMAGE_SORT_OPTIONS.map((option) => option.value));

const IMAGE_LANGUAGE_OPTIONS = [
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

function packStorageKey(packCode, name) {
    return `admin.sliding.packs.${packCode}.${name}`;
}

function loadStoredPageSize(packCode, name) {
    try {
        const stored = parseInt(localStorage.getItem(packStorageKey(packCode, name)), 10);
        if (PAGE_SIZE_OPTIONS.includes(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading sliding pack page size:", error);
    }
    return 15;
}

function loadStoredPage(packCode, name) {
    try {
        const stored = parseInt(localStorage.getItem(packStorageKey(packCode, name)), 10);
        if (Number.isInteger(stored) && stored >= 0) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading sliding pack page:", error);
    }
    return 0;
}

function storePackOption(packCode, name, value) {
    try {
        localStorage.setItem(packStorageKey(packCode, name), String(value));
    } catch (error) {
        Logger.error("Error saving sliding pack option:", error);
    }
}

function loadStoredImageState(packCode) {
    try {
        const stored = localStorage.getItem(packStorageKey(packCode, "imageState"));
        if (stored === null) {
            return "PUBLISHED";
        }
        if (IMAGE_STATE_OPTIONS.some((option) => option.value === stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading sliding pack image state:", error);
    }
    return "PUBLISHED";
}

function loadStoredImageLanguage(packCode) {
    try {
        const stored = localStorage.getItem(packStorageKey(packCode, "imageLanguage"));
        if (IMAGE_LANGUAGE_OPTIONS.some((option) => option.value === stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading sliding pack image language:", error);
    }
    return "english";
}

function loadStoredImageSort(packCode) {
    try {
        const stored = localStorage.getItem(packStorageKey(packCode, "imageSort"));
        if (stored && IMAGE_SORT_VALUES.has(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading sliding pack image sort:", error);
    }
    return "publishedAt:desc";
}

function loadStoredTab(packCode) {
    try {
        const stored = localStorage.getItem(packStorageKey(packCode, "tab"));
        if (PACK_TABS.includes(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading sliding pack tab:", error);
    }
    return "fill";
}

function loadStoredLevelView(packCode) {
    try {
        const stored = localStorage.getItem(packStorageKey(packCode, "levelView"));
        if (stored === "cards" || stored === "list") {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading sliding pack level view:", error);
    }
    return "list";
}

function loadStoredExcludePack(packCode) {
    try {
        return localStorage.getItem(packStorageKey(packCode, "excludePack")) === "true";
    } catch (error) {
        Logger.error("Error reading sliding pack exclude option:", error);
    }
    return false;
}

function storyImage(group) {
    const story = group?.story;
    const candidates = [
        story?.imageUrl,
        story?.image_url,
        story?.imageIcon400Url,
        story?.imageIcon200Url,
        story?.imagePreviewUrl,
    ];
    for (const url of candidates) {
        if (typeof url === "string" && url.trim()) {
            return url.trim();
        }
    }
    return "";
}

function tagOptionValue(option) {
    return typeof option === "string" ? option : (option?.tag ?? option?.name ?? String(option ?? ""));
}

function tagOptionLabel(option) {
    const name = tagOptionValue(option);
    const count = typeof option === "object" && option != null ? option.count : undefined;
    if (count != null && count !== "") {
        return `${name} (${count})`;
    }
    return name;
}

function groupTags(group) {
    const story = group?.story;
    const fromPuzzle = group?.puzzleTemplate?.tags ?? group?.puzzle_template?.tags;
    const source = Array.isArray(fromPuzzle) && fromPuzzle.length > 0 ? fromPuzzle : story?.tags;
    if (!Array.isArray(source)) {
        return [];
    }
    return source
        .map((tag) => String(tag).trim())
        .filter((tag) => tag && tag.toLowerCase() !== "bedrock");
}

function tagButtonLabel(tags) {
    if (!tags.length) return "Tag";
    if (tags.length === 1) return tags[0];
    return `${tags[0]} +${tags.length - 1}`;
}

function storyTitle(group) {
    const story = group?.story;
    if (!story || typeof story !== "object") {
        return "";
    }
    const en = story.english?.title || story.en?.title || "";
    const fr = story.french?.title || story.fr?.title || "";
    return String(en || fr || "").trim();
}

function bundleState(group) {
    return String(group?.storySliding?.state || "").toUpperCase();
}

function openZoomPayload(group, imageUrl, title, storyId) {
    return {
        url: imageUrl,
        title,
        storyId,
        state: bundleState(group) || "PUBLISHED",
        free: getStoryFreeCheckboxState(group).checked,
        standalone: Boolean(group?.story?.standaloneImage),
        templates: group?.templates,
    };
}

function rowsInBand(rows, band) {
    return rows.filter((row) => {
        const level = Number(row.level);
        return level >= band.from && level <= band.to;
    });
}

function savedMovesLabel(matched) {
    const moves = matched
        .map((row) => Number(row.scrambleMoves))
        .filter((value) => Number.isInteger(value));
    if (moves.length === 0) {
        return "—";
    }
    const low = Math.min(...moves);
    const high = Math.max(...moves);
    return low === high ? String(low) : `${low}–${high}`;
}

function PackStats({ bands, rows, loading, error }) {
    const counts = bands.map((band) => {
        const matched = rowsInBand(rows, band);
        return { band, puzzles: matched.length, moves: savedMovesLabel(matched) };
    });
    const total = counts.reduce((sum, item) => sum + item.puzzles, 0);
    const waiting = loading && rows.length === 0;
    return (
        <Box>
            {waiting ? null : (
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {total} {total === 1 ? "puzzle" : "puzzles"}
                </Typography>
            )}
            {waiting ? <CircularProgress size={28} /> : null}
            {error ? <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert> : null}
            {waiting ? null : (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Board</TableCell>
                        <TableCell>Difficulty</TableCell>
                        <TableCell align="right">Scramble moves</TableCell>
                        <TableCell>Levels</TableCell>
                        <TableCell align="right">Puzzles</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {counts.map(({ band, puzzles, moves }) => (
                        <TableRow key={`${band.gridSize}-${band.difficulty}-${band.from}`}>
                            <TableCell>{band.gridSize}×{band.gridSize}</TableCell>
                            <TableCell>{band.scramble}</TableCell>
                            <TableCell align="right">{moves}</TableCell>
                            <TableCell>{band.from}–{band.to}</TableCell>
                            <TableCell align="right">{puzzles}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell colSpan={4} sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{total}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            )}
        </Box>
    );
}

function difficultyLabel(row) {
    const value = String(row?.scramble || row?.difficulty || "").trim().toLowerCase();
    if (value === "lower" || value === "easy") {
        return "Easy";
    }
    if (value === "high" || value === "hard") {
        return "High";
    }
    if (value === "medium") {
        return "Medium";
    }
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
}

function storyIdOf(group) {
    const id = group?.story?.id || group?.storyId;
    return typeof id === "string" ? id.trim() : "";
}

export default function SlidingPackLevelBuilder({ packCode, levelTo, onNotify }) {
    const bands = slidingPackBands(levelTo);
    const [images, setImages] = useState([]);
    const [imageTotal, setImageTotal] = useState(0);
    const [imagePage, setImagePage] = useState(() => loadStoredPage(packCode, "imagePage"));
    const [imagePageSize, setImagePageSize] = useState(() => loadStoredPageSize(packCode, "imagePageSize"));
    const [imageState, setImageState] = useState(() => loadStoredImageState(packCode));
    const [imageSort, setImageSort] = useState(() => loadStoredImageSort(packCode));
    const [imageLanguage, setImageLanguage] = useState(() => loadStoredImageLanguage(packCode));
    const [excludePack, setExcludePack] = useState(() => loadStoredExcludePack(packCode));
    const [fillInfoOpen, setFillInfoOpen] = useState(false);
    const [imagesLoading, setImagesLoading] = useState(true);
    const [imageError, setImageError] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [levels, setLevels] = useState([]);
    const [levelTotal, setLevelTotal] = useState(0);
    const puzzlesNeeded = Math.max(0, levelTo - levelTotal);
    const [levelPage, setLevelPage] = useState(() => loadStoredPage(packCode, "levelPage"));
    const [levelPageSize, setLevelPageSize] = useState(() => loadStoredPageSize(packCode, "levelPageSize"));
    const [levelView, setLevelView] = useState(() => loadStoredLevelView(packCode));
    const [levelsLoading, setLevelsLoading] = useState(true);
    const [tab, setTab] = useState(() => loadStoredTab(packCode));
    const [statsRows, setStatsRows] = useState([]);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState("");
    const [levelError, setLevelError] = useState("");
    const [packedIds, setPackedIds] = useState(() => new Set());
    const [assigning, setAssigning] = useState(false);
    const [fillAllOpen, setFillAllOpen] = useState(false);
    const [firstLevelOpen, setFirstLevelOpen] = useState(false);
    const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
    const [adjusting, setAdjusting] = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [zoomImage, setZoomImage] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [releaseTarget, setReleaseTarget] = useState(null);
    const [releasing, setReleasing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [disabling, setDisabling] = useState(false);
    const [regenDialogOpen, setRegenDialogOpen] = useState(false);
    const [regenLoading, setRegenLoading] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [savingFreeStoryId, setSavingFreeStoryId] = useState("");
    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagDialog, setTagDialog] = useState(null);
    const [tagDraft, setTagDraft] = useState([]);
    const [savingTags, setSavingTags] = useState(false);
    const sortedAvailableTags = useMemo(
        () => [...availableTags].sort((a, b) => tagOptionValue(a).localeCompare(tagOptionValue(b), undefined, { sensitivity: "base" })),
        [availableTags],
    );

    const loadImages = useCallback(async (page, pageSize, state, sort, excludePackCode, language) => {
        setImagesLoading(true);
        setImageError("");
        try {
            const result = await AdminSlidingService.list(
                page,
                pageSize,
                sort || "publishedAt:desc",
                state || undefined,
                undefined,
                language || "english",
                undefined,
                undefined,
                undefined,
                excludePackCode || undefined,
            );
            if (!result?.success) {
                throw new Error(result?.error || "Could not load published sliding puzzles");
            }
            setImages(Array.isArray(result.data?.content) ? result.data.content : []);
            setImageTotal(Number(result.data?.totalElements) || 0);
        } catch (error) {
            Logger.error("Published sliding list failed:", error);
            setImages([]);
            setImageError(error?.message || "Could not load published sliding puzzles");
        } finally {
            setImagesLoading(false);
        }
    }, []);

    const loadLevels = useCallback(async (page, pageSize) => {
        setLevelsLoading(true);
        setLevelError("");
        try {
            const result = await AdminSlidingPackService.levels(packCode, page, pageSize);
            setLevels(Array.isArray(result?.items) ? result.items : []);
            setLevelTotal(Number(result?.total) || 0);
            setPackedIds(new Set(Array.isArray(result?.storyIds) ? result.storyIds.filter(Boolean) : []));
        } catch (error) {
            Logger.error("Sliding pack levels failed:", error);
            setLevels([]);
            setLevelTotal(0);
            setLevelError(error?.message || "Could not load pack levels");
        } finally {
            setLevelsLoading(false);
        }
    }, [packCode]);

    useEffect(() => {
        storePackOption(packCode, "imagePage", imagePage);
    }, [packCode, imagePage]);

    useEffect(() => {
        storePackOption(packCode, "imagePageSize", imagePageSize);
    }, [packCode, imagePageSize]);

    useEffect(() => {
        storePackOption(packCode, "imageState", imageState);
    }, [packCode, imageState]);

    useEffect(() => {
        storePackOption(packCode, "imageSort", imageSort);
    }, [packCode, imageSort]);

    useEffect(() => {
        storePackOption(packCode, "imageLanguage", imageLanguage);
    }, [packCode, imageLanguage]);

    useEffect(() => {
        storePackOption(packCode, "excludePack", excludePack ? "true" : "false");
    }, [packCode, excludePack]);

    useEffect(() => {
        storePackOption(packCode, "levelPage", levelPage);
    }, [packCode, levelPage]);

    useEffect(() => {
        storePackOption(packCode, "levelView", levelView);
    }, [packCode, levelView]);

    useEffect(() => {
        storePackOption(packCode, "levelPageSize", levelPageSize);
    }, [packCode, levelPageSize]);

    useEffect(() => {
        if (imagesLoading) {
            return;
        }
        const maxPage = Math.max(0, Math.ceil(imageTotal / imagePageSize) - 1);
        if (imagePage > maxPage) {
            setImagePage(maxPage);
        }
    }, [imagesLoading, imageTotal, imagePageSize, imagePage]);

    useEffect(() => {
        if (levelsLoading) {
            return;
        }
        const maxPage = Math.max(0, Math.ceil(levelTotal / levelPageSize) - 1);
        if (levelPage > maxPage) {
            setLevelPage(maxPage);
        }
    }, [levelsLoading, levelTotal, levelPageSize, levelPage]);

    useEffect(() => {
        loadImages(imagePage, imagePageSize, imageState, imageSort, excludePack ? packCode : "", imageLanguage);
    }, [imagePage, imagePageSize, imageState, imageSort, excludePack, imageLanguage, packCode, loadImages]);

    useEffect(() => {
        let cancelled = false;
        const loadTags = async () => {
            setTagsLoading(true);
            const result = await AdminSlidingService.getDistinctTags(imageState || undefined, imageLanguage);
            if (cancelled) {
                return;
            }
            setTagsLoading(false);
            setAvailableTags(result?.success ? result.data || [] : []);
        };
        loadTags();
        return () => {
            cancelled = true;
        };
    }, [imageState, imageLanguage]);

    useEffect(() => {
        loadLevels(levelPage, levelPageSize);
    }, [levelPage, levelPageSize, loadLevels]);

    const toggleImage = (id) => {
        setSelectedIds((current) => (
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        ));
    };

    const openTagDialog = (group) => {
        const id = storyIdOf(group);
        if (!id) {
            return;
        }
        setTagDialog({ storyId: id, title: storyTitle(group) || id });
        setTagDraft(groupTags(group));
    };

    const handleSaveTags = async () => {
        const storyId = tagDialog?.storyId;
        if (!storyId || savingTags) {
            return;
        }
        const nextTags = tagDraft
            .map((tag) => tagOptionValue(tag).trim())
            .filter((tag) => tag.length > 0);
        setSavingTags(true);
        const result = await AdminSlidingService.setStoryTags(storyId, nextTags);
        setSavingTags(false);
        if (!result?.success) {
            onNotify("error", result?.error || "Failed to update tags");
            return;
        }
        setImages((current) => current.map((group) => {
            if (storyIdOf(group) !== storyId) {
                return group;
            }
            const puzzleTemplate = group?.puzzleTemplate ?? group?.puzzle_template;
            return {
                ...group,
                story: group?.story ? { ...group.story, tags: nextTags } : group?.story,
                puzzleTemplate: { ...(puzzleTemplate || {}), tags: nextTags },
            };
        }));
        setTagDialog(null);
        setTagDraft([]);
        onNotify("success", "Tags updated");
    };

    const toggleTagDraft = (name) => {
        const normalized = String(name || "").trim();
        if (!normalized) {
            return;
        }
        setTagDraft((current) => {
            const has = current.some((tag) => tagOptionValue(tag).toLowerCase() === normalized.toLowerCase());
            if (has) {
                return current.filter((tag) => tagOptionValue(tag).toLowerCase() !== normalized.toLowerCase());
            }
            return [normalized];
        });
    };

    const handlePublish = async () => {
        const storyId = zoomImage?.storyId;
        if (!storyId || publishing || deleting) {
            return;
        }
        if (Array.isArray(zoomImage.templates) && zoomImage.templates.length === 0) {
            onNotify("error", "No sliding templates to publish");
            return;
        }
        setPublishing(true);
        const result = await AdminSlidingService.publishStory(storyId);
        setPublishing(false);
        if (!result?.success) {
            onNotify("error", result?.error || "Publish failed");
            return;
        }
        setZoomImage((current) => (current?.storyId === storyId ? { ...current, state: "PUBLISHED" } : current));
        setImages((current) => current.map((group) => {
            if (storyIdOf(group) !== storyId) {
                return group;
            }
            return {
                ...group,
                storySliding: { ...(group.storySliding || {}), state: "PUBLISHED" },
            };
        }));
        onNotify("success", "Published successfully");
    };

    const handleConfirmDelete = async (storyId) => {
        if (!storyId || deleting) {
            return;
        }
        setDeleting(true);
        const result = await AdminSlidingService.removeByStoryId(storyId);
        setDeleting(false);
        if (!result?.success) {
            onNotify("error", result?.error || "Delete failed");
            return;
        }
        setZoomImage(null);
        setImages((current) => current.filter((group) => storyIdOf(group) !== storyId));
        setSelectedIds((current) => current.filter((id) => id !== storyId));
        setImageTotal((current) => Math.max(0, current - 1));
        onNotify("success", "Sliding puzzle deleted");
        try {
            await AdminSlidingPackService.releaseStory(packCode, storyId);
        } catch (error) {
            onNotify("error", error?.message || "Puzzle deleted, but it is still in the pack");
        }
        setPackedIds((current) => {
            const next = new Set(current);
            next.delete(storyId);
            return next;
        });
        loadLevels(levelPage, levelPageSize);
    };

    const handleRelease = async (storyId) => {
        if (!storyId || releasing) {
            return;
        }
        setReleasing(true);
        try {
            await AdminSlidingPackService.releaseStory(packCode, storyId);
            setPackedIds((current) => {
                const next = new Set(current);
                next.delete(storyId);
                return next;
            });
            onNotify("success", "Puzzle released from the pack");
            loadLevels(levelPage, levelPageSize);
            loadStats();
        } catch (error) {
            onNotify("error", error?.message || "Failed to release puzzle");
        } finally {
            setReleasing(false);
        }
    };

    const handleStoryFreeChange = async (storyId, free) => {
        setSavingFreeStoryId(storyId);
        const result = await AdminSlidingService.setStoryTemplatesFree(storyId, free);
        setSavingFreeStoryId("");
        if (!result?.success) {
            onNotify("error", result?.error || "Failed to update free flag");
            return;
        }
        setImages((current) => current.map((group) => {
            if (storyIdOf(group) !== storyId) {
                return group;
            }
            return {
                ...group,
                storySliding: { ...(group.storySliding || {}), free },
            };
        }));
        setZoomImage((current) => (current?.storyId === storyId ? { ...current, free } : current));
        onNotify("success", free ? "Marked free" : "Marked paid");
    };

    const handleConfirmDisable = async (storyId) => {
        if (!storyId || disabling) {
            return;
        }
        setDisabling(true);
        const result = await AdminSlidingService.disableStory(storyId);
        setDisabling(false);
        if (!result?.success) {
            onNotify("error", result?.error || "Disable failed");
            return;
        }
        setZoomImage((current) => (current ? { ...current, state: "DISABLED" } : current));
        setImages((current) => current.filter((group) => storyIdOf(group) !== storyId));
        setSelectedIds((current) => current.filter((id) => id !== storyId));
        onNotify("success", "Puzzle disabled. Players cannot access it. It will be discarded in 1 week.");
        loadImages(imagePage, imagePageSize, imageState, imageSort, excludePack ? packCode : "", imageLanguage);
        loadLevels(levelPage, levelPageSize);
    };

    const handleConfirmRegenImage = async (storyId) => {
        if (!storyId || regenLoading) {
            return;
        }
        setRegenLoading(true);
        try {
            const result = await AdminStoryService.regenImage(storyId);
            if (!result?.success) {
                onNotify("error", result?.error || "Regenerate image failed");
                return;
            }
            const nextUrl = storyImage({ story: result.data }) || zoomImage?.url || "";
            const busted = nextUrl
                ? `${nextUrl}${nextUrl.includes("?") ? "&" : "?"}v=${Date.now()}`
                : "";
            if (busted) {
                setZoomImage((current) => (current ? { ...current, url: busted } : current));
                setImages((current) => current.map((group) => {
                    if (storyIdOf(group) !== storyId || !group.story) {
                        return group;
                    }
                    return { ...group, story: { ...group.story, imageUrl: busted } };
                }));
            }
            const preview = await AdminSlidingService.genPreview(storyId);
            onNotify(
                preview?.success ? "success" : "warning",
                preview?.success
                    ? "Image replaced. Sliding puzzle images were regenerated."
                    : "Image replaced. Sliding preview refresh failed — use Gen preview if needed.",
            );
        } catch (error) {
            Logger.error("Sliding image regen failed:", error);
            onNotify("error", error?.message || "Regenerate image failed");
        } finally {
            setRegenLoading(false);
        }
    };

    const handleAssign = async () => {
        const freshIds = selectedIds.filter((id) => !packedIds.has(id));
        if (selectedIds.length === 0) {
            onNotify("error", "Select at least one image");
            return;
        }
        if (freshIds.length === 0) {
            onNotify("error", "Those images are already in the pack");
            return;
        }
        if (levelTotal >= levelTo) {
            onNotify("error", "The pack is full");
            return;
        }
        setAssigning(true);
        try {
            await AdminSlidingPackService.assignLevels(packCode, freshIds);
            window.location.reload();
        } catch (error) {
            Logger.error("Sliding pack assign failed:", error);
            onNotify("error", error?.message || "Could not fill pack levels");
        } finally {
            setAssigning(false);
        }
    };

    const handleSetFirstLevel = async () => {
        if (selectedIds.length !== 1) {
            onNotify("error", "Select one image");
            return;
        }
        setAssigning(true);
        try {
            await AdminSlidingPackService.setFirstLevel(packCode, selectedIds[0]);
            window.location.reload();
        } catch (error) {
            Logger.error("Sliding pack set first level failed:", error);
            onNotify("error", error?.message || "Could not set level 1");
        } finally {
            setAssigning(false);
        }
    };

    const handleFillAutomatically = async () => {
        if (levelTotal >= levelTo) {
            onNotify("error", "This pack is already full");
            return;
        }
        setAssigning(true);
        try {
            await AdminSlidingPackService.fillAutomatically(packCode, imageLanguage);
            window.location.reload();
        } catch (error) {
            Logger.error("Sliding pack automatic fill failed:", error);
            onNotify("error", error?.message || "Could not fill pack levels");
        } finally {
            setAssigning(false);
        }
    };

    const openLevelZoom = (row) => {
        if (!row?.imageUrl) {
            return;
        }
        const group = images.find((item) => storyIdOf(item) === row.storyId);
        setZoomImage(group
            ? openZoomPayload(group, row.imageUrl, row.title || storyTitle(group) || row.storyId, row.storyId)
            : {
                url: row.imageUrl,
                title: row.title || `Level ${row.level}`,
                storyId: row.storyId,
                state: "PUBLISHED",
                free: false,
                templates: [],
            });
    };

    const handleAdjustLayouts = async () => {
        setAdjusting(true);
        try {
            const result = await AdminSlidingPackService.adjustLayouts(packCode, selectedIds);
            const adjusted = Number(result?.adjusted) || 0;
            const removed = Number(result?.removedDuplicates) || 0;
            const added = Number(result?.added) || 0;
            const removedNote = removed > 0 ? ` Removed ${removed} duplicate puzzles.` : "";
            const addedNote = added > 0 ? ` Added ${added}.` : "";
            onNotify("success", `${adjusted} puzzles remain.${removedNote}${addedNote}`);
            await loadLevels(levelPage, levelPageSize);
            await loadStats();
        } catch (error) {
            Logger.error("Sliding pack layout adjust failed:", error);
            onNotify("error", error?.message || "Could not adjust starting layouts");
        } finally {
            setAdjusting(false);
        }
    };

    const handleReset = async () => {
        setResetting(true);
        try {
            const result = await AdminSlidingPackService.reset(packCode);
            const removed = Number(result?.removed) || 0;
            onNotify("success", removed === 1 ? "1 puzzle removed." : `${removed} puzzles removed.`);
            setLevelPage(0);
            await loadLevels(0, levelPageSize);
            await loadStats();
        } catch (error) {
            Logger.error("Sliding pack reset failed:", error);
            onNotify("error", error?.message || "Could not reset the pack");
        } finally {
            setResetting(false);
        }
    };

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        setStatsError("");
        try {
            const pageSize = 100;
            const rows = [];
            let page = 0;
            let total = 0;
            do {
                const result = await AdminSlidingPackService.levels(packCode, page, pageSize);
                const items = Array.isArray(result?.items) ? result.items : [];
                total = Number(result?.total) || 0;
                rows.push(...items);
                page += 1;
                if (items.length === 0) {
                    break;
                }
            } while (rows.length < total);
            setStatsRows(rows);
        } catch (error) {
            Logger.error("Sliding pack stats failed:", error);
            setStatsRows([]);
            setStatsError(error?.message || "Could not load pack stats");
        } finally {
            setStatsLoading(false);
        }
    }, [packCode]);

    useEffect(() => {
        if (tab !== "stats" && tab !== "check") {
            return undefined;
        }
        loadStats();
        return undefined;
    }, [tab, loadStats]);

    const changeTab = (_event, next) => {
        if (!PACK_TABS.includes(next)) {
            return;
        }
        setTab(next);
        storePackOption(packCode, "tab", next);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Tabs
                value={tab}
                onChange={changeTab}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
                <Tab
                    value="fill"
                    label={levelsLoading ? "Fill level" : `Fill level · ${puzzlesNeeded} more`}
                    sx={{ minHeight: 44, textTransform: "none" }}
                />
                <Tab value="check" label="Check level" sx={{ minHeight: 44, textTransform: "none" }} />
                <Tab value="stats" label="Stats" sx={{ minHeight: 44, textTransform: "none" }} />
            </Tabs>
            {tab === "fill" ? (
            <>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Images
            </Typography>
            {!levelsLoading ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                    {levelTotal} of {levelTo} puzzles in the pack. {puzzlesNeeded} more needed.
                </Alert>
            ) : null}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="sliding-pack-image-state">State</InputLabel>
                    <Select
                        labelId="sliding-pack-image-state"
                        label="State"
                        value={imageState}
                        onChange={(event) => {
                            setImageState(event.target.value);
                            setImagePage(0);
                        }}
                        sx={{ minHeight: 44 }}
                    >
                        {IMAGE_STATE_OPTIONS.map((option) => (
                            <MenuItem key={option.label} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="sliding-pack-image-sort">Order by</InputLabel>
                    <Select
                        labelId="sliding-pack-image-sort"
                        label="Order by"
                        value={imageSort}
                        onChange={(event) => {
                            setImageSort(event.target.value);
                            setImagePage(0);
                        }}
                        sx={{ minHeight: 44 }}
                    >
                        {IMAGE_SORT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="sliding-pack-image-language">Language</InputLabel>
                    <Select
                        labelId="sliding-pack-image-language"
                        label="Language"
                        value={imageLanguage}
                        onChange={(event) => {
                            setImageLanguage(event.target.value);
                            setImagePage(0);
                            setSelectedIds([]);
                        }}
                        sx={{ minHeight: 44 }}
                    >
                        {IMAGE_LANGUAGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={excludePack}
                            onChange={(event) => {
                                const next = event.target.checked;
                                setExcludePack(next);
                                setImagePage(0);
                                if (next) {
                                    setSelectedIds((current) => current.filter((id) => !packedIds.has(id)));
                                }
                            }}
                        />
                    )}
                    label="Exclude pack"
                    sx={{ minHeight: 44, ml: 0.5 }}
                />
                <Tooltip title="How levels are filled">
                    <IconButton
                        size="small"
                        aria-label="How levels are filled"
                        onClick={() => setFillInfoOpen(true)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                    >
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            {imagesLoading ? <CircularProgress size={28} /> : null}
            {imageError ? <Alert severity="error" sx={{ mb: 1 }}>{imageError}</Alert> : null}
            <Grid container spacing={1.5}>
                {images.map((group) => {
                    const id = storyIdOf(group);
                    const packed = packedIds.has(id);
                    if (excludePack && packed) {
                        return null;
                    }
                    const selectedIndex = selectedIds.indexOf(id);
                    const selected = selectedIndex >= 0;
                    const imageUrl = storyImage(group);
                    const title = storyTitle(group) || id;
                    const tags = groupTags(group);
                    return (
                        <Grid key={id || title} size={{ xs: 6, sm: 4, md: 3 }}>
                            <Box
                                sx={{
                                    border: "2px solid",
                                    borderColor: selected ? "primary.main" : "transparent",
                                    borderRadius: 1,
                                    p: 0.5,
                                }}
                            >
                                <Typography variant="body2" noWrap title={title} sx={{ mb: 0.5, fontWeight: 600 }}>
                                    {selected ? `${selectedIndex + 1}. ` : ""}{title}
                                </Typography>
                                <Box
                                    onClick={() => id && toggleImage(id)}
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: "grey.900",
                                        cursor: id ? "pointer" : "default",
                                    }}
                                >
                                    {imageUrl ? (
                                        <Box
                                            component="img"
                                            src={imageUrl}
                                            alt={title}
                                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                        />
                                    ) : (
                                        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <Typography variant="caption" color="grey.500">No image</Typography>
                                        </Box>
                                    )}
                                    {packed ? (
                                        <Chip
                                            size="small"
                                            label="Packed"
                                            color="primary"
                                            sx={{
                                                position: "absolute",
                                                top: 8,
                                                left: 8,
                                                zIndex: 2,
                                                fontWeight: 700,
                                                pointerEvents: "none",
                                            }}
                                        />
                                    ) : null}
                                </Box>
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
                                    <Button
                                        size="small"
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<LocalOfferOutlinedIcon />}
                                        disabled={!id}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            openTagDialog(group);
                                        }}
                                        sx={{ minHeight: 44, textTransform: "none", justifyContent: "flex-start", px: 1, minWidth: 0 }}
                                    >
                                        <Typography component="span" variant="body2" noWrap>
                                            {tagButtonLabel(tags)}
                                        </Typography>
                                    </Button>
                                    <Tooltip title="Zoom image">
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label={`Zoom ${title}`}
                                                disabled={!imageUrl}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    if (!imageUrl) {
                                                        return;
                                                    }
                                                    setZoomImage(openZoomPayload(group, imageUrl, title, id));
                                                }}
                                                sx={{ width: 44, height: 44, flexShrink: 0 }}
                                            >
                                                <ZoomInIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
            <TablePagination
                component="div"
                count={imageTotal}
                page={imagePage}
                onPageChange={(_event, next) => setImagePage(next)}
                rowsPerPage={imagePageSize}
                onRowsPerPageChange={(event) => {
                    const next = parseInt(event.target.value, 10);
                    if (!PAGE_SIZE_OPTIONS.includes(next)) {
                        return;
                    }
                    setImagePageSize(next);
                    setImagePage(0);
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
                <Button
                    variant="contained"
                    onClick={() => setFillAllOpen(true)}
                    disabled={assigning || levelsLoading || levelTotal >= levelTo}
                    sx={{ minHeight: 44 }}
                >
                    Fill automatically
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleAssign}
                    disabled={assigning || selectedIds.length === 0}
                    sx={{ minHeight: 44 }}
                >
                    Fill selected puzzle
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => setFirstLevelOpen(true)}
                    disabled={assigning || selectedIds.length !== 1}
                    sx={{ minHeight: 44 }}
                >
                    Set first level
                </Button>
                <Button
                    onClick={() => setSelectedIds([])}
                    disabled={assigning || selectedIds.length === 0}
                    sx={{ minHeight: 44 }}
                >
                    Clear selection
                </Button>
                <Typography variant="body2">{selectedIds.length} selected</Typography>
            </Box>
            </>
            ) : tab === "check" ? (
            <>
            <Box sx={{ mb: 2 }}>
                {statsLoading && statsRows.length === 0 ? <CircularProgress size={28} /> : null}
                {statsError ? <Alert severity="warning">{statsError}</Alert> : null}
                {statsLoading && statsRows.length === 0 ? null : (
                    <Typography sx={{ fontWeight: 600 }}>
                        {statsRows.length} {statsRows.length === 1 ? "puzzle" : "puzzles"}
                        {statsRows.length < levelTo
                            ? `. ${levelTo - statsRows.length} needed`
                            : ""}
                    </Typography>
                )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1, mt: 2, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 600 }}>Levels</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={levelView}
                        onChange={(_event, next) => {
                            if (next) {
                                setLevelView(next);
                            }
                        }}
                        aria-label="Level view"
                    >
                        <ToggleButton value="list" aria-label="List" sx={{ minHeight: 44, px: 1.5 }}>
                            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
                            List
                        </ToggleButton>
                        <ToggleButton value="cards" aria-label="Cards" sx={{ minHeight: 44, px: 1.5 }}>
                            <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Cards
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={resetting || adjusting || levelsLoading || levelTotal === 0}
                        onClick={() => setResetDialogOpen(true)}
                        sx={{ minHeight: 44 }}
                    >
                        {resetting ? <CircularProgress size={22} /> : "Reset"}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={adjusting || resetting || levelsLoading || (levelTotal === 0 && selectedIds.length === 0)}
                        onClick={() => setAdjustDialogOpen(true)}
                        sx={{ minHeight: 44 }}
                    >
                        {adjusting ? <CircularProgress size={22} /> : "Rearrange"}
                    </Button>
                </Box>
            </Box>
            {levelsLoading ? <CircularProgress size={28} /> : null}
            {levelError ? <Alert severity="warning" sx={{ mb: 1 }}>{levelError}</Alert> : null}
            {!levelsLoading && !levelError && levels.length === 0 ? (
                <Alert severity="info">No levels yet. Select images, then fill levels 1–{levelTo}.</Alert>
            ) : null}
            {levels.length > 0 && levelView === "list" ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Level</TableCell>
                            <TableCell>Image</TableCell>
                            <TableCell>Board</TableCell>
                            <TableCell>Scramble</TableCell>
                            <TableCell align="right">Moves</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {levels.map((row) => (
                            <TableRow key={row.level}>
                                <TableCell>{row.level}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {row.imageUrl ? (
                                            <Box
                                                component="img"
                                                src={row.imageUrl}
                                                alt={row.title || "Sliding image"}
                                                sx={{ width: 48, height: 48, objectFit: "cover", borderRadius: 0.5 }}
                                            />
                                        ) : null}
                                        <Tooltip title="Release from pack">
                                            <IconButton
                                                size="small"
                                                aria-label={`Release level ${row.level} from pack`}
                                                onClick={() => setReleaseTarget(row)}
                                                sx={{ width: 44, height: 44 }}
                                            >
                                                <LockOpenOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Zoom image">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Zoom level ${row.level}`}
                                                    disabled={!row.imageUrl}
                                                    onClick={() => openLevelZoom(row)}
                                                    sx={{ width: 44, height: 44 }}
                                                >
                                                    <ZoomInIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Typography variant="body2">{row.title || row.storyId}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>{row.gridSize}×{row.gridSize}</TableCell>
                                <TableCell>{difficultyLabel(row)}</TableCell>
                                <TableCell align="right">{row.scrambleMoves ?? "—"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : null}
            {levels.length > 0 && levelView === "cards" ? (
                <Grid container spacing={1.5}>
                    {levels.map((row) => (
                        <Grid key={row.level} size={{ xs: 6, sm: 4, md: 3 }}>
                            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 0.5 }}>
                                <Typography variant="body2" noWrap title={row.title || row.storyId} sx={{ mb: 0.5, fontWeight: 600 }}>
                                    {row.level}. {row.title || row.storyId}
                                </Typography>
                                <Box
                                    onClick={() => openLevelZoom(row)}
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: "grey.900",
                                        cursor: row.imageUrl ? "pointer" : "default",
                                    }}
                                >
                                    {row.imageUrl ? (
                                        <Box
                                            component="img"
                                            src={row.imageUrl}
                                            alt={row.title || "Sliding image"}
                                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                        />
                                    ) : (
                                        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <Typography variant="caption" color="grey.500">No image</Typography>
                                        </Box>
                                    )}
                                    <Tooltip title="Release from pack">
                                        <IconButton
                                            aria-label={`Release level ${row.level} from pack`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setReleaseTarget(row);
                                            }}
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                left: 4,
                                                zIndex: 2,
                                                width: 44,
                                                height: 44,
                                                color: "#fff",
                                                bgcolor: "rgba(0,0,0,0.55)",
                                                "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                                            }}
                                        >
                                            <LockOpenOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
                                    <Typography variant="body2">
                                        {row.gridSize}×{row.gridSize}
                                        {" · "}
                                        <Box component="span">{difficultyLabel(row)}</Box>
                                        {row.scrambleMoves != null ? ` · ${row.scrambleMoves}` : ""}
                                    </Typography>
                                    <Tooltip title="Zoom image">
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label={`Zoom level ${row.level}`}
                                                disabled={!row.imageUrl}
                                                onClick={() => openLevelZoom(row)}
                                                sx={{ width: 44, height: 44 }}
                                            >
                                                <ZoomInIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            ) : null}
            {levels.length > 0 ? (
                <TablePagination
                    component="div"
                    count={levelTotal}
                    page={levelPage}
                    onPageChange={(_event, next) => setLevelPage(next)}
                    rowsPerPage={levelPageSize}
                    onRowsPerPageChange={(event) => {
                        const next = parseInt(event.target.value, 10);
                        if (!PAGE_SIZE_OPTIONS.includes(next)) {
                            return;
                        }
                        setLevelPageSize(next);
                        setLevelPage(0);
                    }}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                />
            ) : null}
            </>
            ) : (
            <PackStats
                bands={bands}
                rows={statsRows}
                loading={statsLoading}
                error={statsError}
            />
            )}

            <Dialog open={fillInfoOpen} onClose={() => setFillInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>How levels are filled</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Fill automatically adds published sliding puzzles that are not already in the pack, one each. It does not repeat them, and it does not use the selection. It is available when the pack is not full. Fill selected puzzle adds only the images you selected, one level each. The board follows the level number, from a 4×4 lower scramble up to a 6×6 high scramble.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {bands.map((band) => (
                            <Typography key={`${band.from}-${band.to}`} variant="body2">
                                {bandLabel(band)}
                            </Typography>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFillInfoOpen(false)} sx={{ minHeight: 44 }}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(tagDialog)} onClose={() => !savingTags && setTagDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Update tag</DialogTitle>
                <DialogContent dividers>
                    {tagDialog?.title ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tagDialog.title}
                        </Typography>
                    ) : null}
                    {tagsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : sortedAvailableTags.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No tags available.</Typography>
                    ) : (
                        <List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
                            {sortedAvailableTags.map((option) => {
                                const name = tagOptionValue(option);
                                if (!name) {
                                    return null;
                                }
                                const picked = tagDraft.some((tag) => tagOptionValue(tag).toLowerCase() === name.toLowerCase());
                                return (
                                    <ListItemButton
                                        key={name}
                                        selected={picked}
                                        disabled={savingTags}
                                        onClick={() => toggleTagDraft(name)}
                                        sx={{ minHeight: 44 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            {picked ? <CheckIcon fontSize="small" color="success" /> : null}
                                        </ListItemIcon>
                                        <ListItemText primary={tagOptionLabel(option)} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTagDialog(null)} disabled={savingTags} sx={{ minHeight: 44 }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveTags} disabled={savingTags} sx={{ minHeight: 44 }}>
                        {savingTags ? <CircularProgress size={22} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(releaseTarget)}
                setOpen={(open) => {
                    if (!open) {
                        setReleaseTarget(null);
                    }
                }}
                title="Release from pack?"
                targetName={releaseTarget?.title ? `'${releaseTarget.title}'` : "This puzzle"}
                targetId={releaseTarget?.storyId}
                message="This puzzle is removed from every level in the pack. The sliding puzzle itself stays."
                callback={handleRelease}
                okText="Release"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={firstLevelOpen}
                setOpen={setFirstLevelOpen}
                title="Set first level?"
                targetName={packCode}
                targetId={packCode}
                message="This image becomes level 1. The puzzle already at level 1 is replaced. If this image is already on another level, it is moved to level 1."
                callback={handleSetFirstLevel}
                okText="Set first level"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={fillAllOpen}
                setOpen={setFillAllOpen}
                title="Fill automatically?"
                targetName={packCode}
                targetId={packCode}
                message="This adds published sliding puzzles that are not already in the pack, one each, into the empty levels. A puzzle is not repeated to fill the pack. The selection is not used."
                callback={handleFillAutomatically}
                okText="Fill"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={resetDialogOpen}
                setOpen={setResetDialogOpen}
                title="Reset pack?"
                targetName={packCode}
                targetId={packCode}
                message="This removes every puzzle from the pack. The images stay published."
                callback={handleReset}
                okText="Reset"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={adjustDialogOpen}
                setOpen={setAdjustDialogOpen}
                title="Rearrange?"
                targetName={packCode}
                targetId={packCode}
                message={[
                    "Duplicate images are removed. One copy of each image stays.",
                    "Each remaining level gets a new starting layout. Fewer scramble moves come first.",
                    "Selected images that are not already in the pack are added at the end.",
                ]}
                callback={handleAdjustLayouts}
                okText="Rearrange"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Delete sliding puzzle?"
                targetName={zoomImage?.title ? `'${zoomImage.title}'` : "This sliding puzzle"}
                targetId={zoomImage?.storyId}
                message={[
                    "This permanently deletes all sliding templates, preview images, and play history for this story.",
                    "Sliding is discarded so scheduled jobs will not recreate it. The story record stays.",
                    "The linked crossword, word-search, or jigsaw puzzle is not deleted.",
                    "This action cannot be undone.",
                ]}
                callback={handleConfirmDelete}
            />

            <ConfirmDialog
                open={regenDialogOpen}
                setOpen={setRegenDialogOpen}
                title="Regenerate image?"
                message="This replaces the current illustration and regenerates sliding puzzle images. Story text is not changed."
                targetName={zoomImage?.title ? `'${zoomImage.title}'` : "This sliding puzzle"}
                targetId={zoomImage?.storyId}
                callback={handleConfirmRegenImage}
                warningAlert="Would you like to re-generate this image? Did you check the players who played this puzzle."
                okText="OK"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable this puzzle?"
                message="Players will not be able to open or continue this puzzle. It stays disabled for 1 week, then it is discarded. Linked jigsaw and word puzzles for this story are disabled too."
                targetName={zoomImage?.title ? `'${zoomImage.title}'` : "This sliding puzzle"}
                targetId={zoomImage?.storyId}
                callback={handleConfirmDisable}
                warningAlert="Not available. Will be removed in 1 week."
                okText="Disable"
                cancelText="Cancel"
            />

            <StoryImageZoomDialog
                open={Boolean(zoomImage?.url)}
                imageUrl={zoomImage?.url || ""}
                title={zoomImage?.title}
                onClose={() => !regenLoading && !deleting && !disabling && !publishing && setZoomImage(null)}
                onDelete={zoomImage?.storyId ? () => setDeleteDialogOpen(true) : undefined}
                deleting={deleting}
                onDisable={
                    zoomImage?.storyId && String(zoomImage?.state || "").toUpperCase() === "PUBLISHED"
                        ? () => setDisableDialogOpen(true)
                        : undefined
                }
                disabling={disabling}
                onRegen={zoomImage?.storyId ? () => setRegenDialogOpen(true) : undefined}
                regenLoading={regenLoading}
                free={zoomImage?.free === true}
                onFreeChange={
                    zoomImage?.storyId
                        ? (checked) => handleStoryFreeChange(zoomImage.storyId, checked)
                        : undefined
                }
                savingFree={savingFreeStoryId === zoomImage?.storyId}
                state={zoomImage?.state}
                standalone={zoomImage?.standalone === true}
                onPublish={zoomImage?.storyId ? handlePublish : undefined}
                publishing={publishing}
                extra={zoomImage?.storyId ? <SlidingPlaySummaryRow storyId={zoomImage.storyId} /> : null}
            />
        </Box>
    );
}
